// Page-wide UI state shared between the filter bar, table headers, pagination,
// and the full-page form view. Server data lives in React Query — this store
// only tracks transient screen state (PRD 13 §2).
//
// The notice form is a full-page swap (per the prototype, not a slide panel):
// `view` drives whether the list or the create/edit form renders.

import { create } from 'zustand';

// PRD 13 §4.1 sortable columns. `author` is non-sortable per the prototype.
export type NoticeSortKey =
  | 'id'
  | 'audience'
  | 'title'
  | 'category'
  | 'views'
  | 'createdAt'
  | 'status';
export type SortDir = 'asc' | 'desc';

// `null` = list view. `{ mode: 'create' }` opens an empty form; `{ mode:
// 'edit', id }` opens the form hydrated with that notice's detail.
export type NoticeView =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; id: number };

interface NoticesPageState {
  search: string;
  sortKey: NoticeSortKey;
  sortDir: SortDir;
  page: number;
  view: NoticeView;

  // Search change resets to page 1 (prototype parity). Sort change keeps the
  // current page intact.
  setSearch: (q: string) => void;
  toggleSort: (key: NoticeSortKey) => void;
  setPage: (p: number) => void;
  openCreate: () => void;
  openEdit: (id: number) => void;
  closeForm: () => void;
  reset: () => void;
}

// PRD 13 §3.1 default sort = createdAt DESC, first page, list view.
const INITIAL = {
  search: '',
  sortKey: 'createdAt' as const,
  sortDir: 'desc' as const,
  page: 1,
  view: null as NoticeView,
};

export const useNoticesPageStore = create<NoticesPageState>((set) => ({
  ...INITIAL,
  setSearch: (q) => set({ search: q, page: 1 }),
  toggleSort: (key) =>
    set((s) =>
      s.sortKey === key
        ? { sortDir: s.sortDir === 'asc' ? 'desc' : 'asc' }
        : { sortKey: key, sortDir: 'asc' },
    ),
  setPage: (p) => set({ page: Math.max(1, p) }),
  openCreate: () => set({ view: { mode: 'create' } }),
  openEdit: (id) => set({ view: { mode: 'edit', id } }),
  closeForm: () => set({ view: null }),
  reset: () => set(INITIAL),
}));
